import React, { useCallback, useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import CreateOfferButton from "./CreateOfferButton";
import { getOfferEditHref, offerColumns } from "./tableParams/offerColumns";
import OfferAPI from "../../api/OfferAPI";
import useAbility from "../../hooks/useAbility";
import ItemStatus from "../../utils/ItemStatus";
import CappedPage from "../common/CappedPage";
import DataTable from "../dataTable/DataTable";
import DataTableEmptyState from "../dataTable/DataTableEmptyState";
import { offerViewPath } from "../routes/routes";
import Breadcrumbs from "../utils/Breadcrumbs";

import type { JSX } from "react";

import type { GetOfferListResponse } from "../../api/OfferAPI.types";
import type { AppState } from "../../reducers";
import type { OfferRow } from "./tableParams/offerColumns.types";

function OfferList(): JSX.Element {
  const { t } = useTranslation("editor-offers");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const queryClient = useQueryClient();
  const history = useHistory();
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );

  const { canAccess } = useAbility();
  const { project: canAccessProject } = canAccess(orgaOptions);

  const getOffersQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getOffers,
    params: [{ projectSlug }],
  });

  const {
    data: offersData,
    isLoading,
    isError,
    refetch,
  } = useQuery(getOffersQuery);

  const offerRowList = useMemo(
    (): OfferRow[] =>
      (offersData?.data ?? []).map((offer) => ({
        ...offer,
        status: ItemStatus.getOfferStatus(offer),
      })),
    [offersData?.data],
  );

  const { mutate: deleteOffer } = useMutation({
    mutationFn: async (uuid: string): Promise<unknown> =>
      OfferAPI.deleteOffer({ uuid, projectSlug }),
    onMutate: async (uuid) => {
      await queryClient.cancelQueries(getOffersQuery.queryKey);

      const previousData = queryClient.getQueryData<GetOfferListResponse>(
        getOffersQuery.queryKey,
      );

      queryClient.setQueryData<GetOfferListResponse>(
        getOffersQuery.queryKey,
        (old) =>
          old && {
            ...old,
            data: old.data.filter((offer) => offer.uuid !== uuid),
          },
      );

      return { previousData };
    },
    onSuccess: () => {
      toast.success(t("deleteOfferSuccess"));
    },
    onError: (_error, _uuid, context) => {
      queryClient.setQueryData(getOffersQuery.queryKey, context?.previousData);
      toast.error(t("deleteOfferError"));
    },
    onSettled: async () =>
      queryClient.invalidateQueries(getOffersQuery.queryKey),
  });

  const getRowHref = useCallback(
    (offer: OfferRow): string => getOfferEditHref(projectSlug, offer.uuid),
    [projectSlug],
  );

  const columnList = useMemo(
    () =>
      offerColumns({
        t,
        projectSlug,
        offerCount: offerRowList.length,
        canWrite: canAccessProject.offersWrite,
        canViewStats: canAccessProject.offersView,
        onEdit: (offer) => history.push(getRowHref(offer)),
        onViewStats: (offer) =>
          history.push(offerViewPath(projectSlug, offer.uuid)),
        onDelete: (offer) => deleteOffer(offer.uuid),
      }),
    [
      t,
      projectSlug,
      offerRowList.length,
      canAccessProject.offersWrite,
      canAccessProject.offersView,
      history,
      getRowHref,
      deleteOffer,
    ],
  );

  const breadcrumbList = [
    {
      name: t("editor-menu:offerManagement"),
      url: "/offers",
    },
    {
      name: t("offersTitle"),
    },
  ];

  const emptyState = (
    <DataTableEmptyState
      action={<CreateOfferButton />}
      icon={CreditCard}
      message={t("createOfferText")}
      title={t("offersTitle")}
    />
  );

  return (
    <CappedPage contentClassName="pt-7.5 pb-15">
      <Breadcrumbs items={breadcrumbList} />
      <DataTable
        columns={columnList}
        data={offerRowList}
        defaultSorting={{ id: "updated_at", desc: true }}
        emptyState={emptyState}
        getRowHref={canAccessProject.offersWrite ? getRowHref : undefined}
        headerActions={<CreateOfferButton />}
        isError={isError}
        isLoading={isLoading}
        onRetry={refetch}
        title={t("offersTitle")}
      />
    </CappedPage>
  );
}

export default OfferList;
